import { CallAdapter } from './CallAdapter';
import { HttpClient } from '../HttpClient';
import { HttpResponse } from '../HttpResponse';
import { HttpRequest } from '../HttpRequest';
import { ErrorCodes } from '../../error/ErrorCodes';
import { createError } from '../../error/Error';

type Payload = string | Blob | ArrayBuffer | FormData | undefined;

/**
 * Performs HTTP requests.
 *
 * @usageNotes
 * Sample HTTP requests
 *
 * ### HTTP Request Example
 * ```
 * const sampleRequest = flFoundation.requestBuilder();
 * const request = sampleRequest
 *  .url('someurl')
 *  .path('/get')
 *  .get()
 *  .headers({ accept: 'application/json' })
 *  .queryParameters({ foo: 'bar'})
 *  .build();
 *
 * const httpClient = flFoundation.httpClient();
 * const response = await httpClient.callWith(request);
 *
 * ```
 *
 * @publicApi
 */
export class HttpClientAdapter<RequestBody, ResponseBody> implements HttpClient<RequestBody, ResponseBody> {
  private requestTimeoutMS_!: number;

  private call_!: CallAdapter<RequestBody, ResponseBody>;
  public get requestTimeoutMS(): number {
    return this.requestTimeoutMS_;
  }
  public set requestTimeoutMS(value: number) {
    this.requestTimeoutMS_ = value || 60 * 1000;
  }

  /**
   * HTTP network request is made using the callWith() method which accepts request build
   * using the `RequestBuilder`.
   *
   * @method Method callWith
   *
   * @param request Accepts request [{@link HttpRequest}] constructed using the request builder [{@link RequestBuilder}].
   *
   * @returns Returns a promise with typed response or error [{@link HttpResponse}].
   */
  callWith(request: HttpRequest<RequestBody>): Promise<HttpResponse<ResponseBody>> {
    this.call_ = new CallAdapter();
    this.call_.request = request;
    this.requestTimeoutMS = request.timeout ? request.timeout : this.requestTimeoutMS_;
    return this.performRequest_();
  }

  private performRequest_ = (): Promise<HttpResponse<ResponseBody>> => {
    const requestUrl: string = this.call_.request.url;
    const TIMEOUT = this.requestTimeoutMS;

    let abortController: AbortController | undefined;
    if (typeof (window as any).AbortController === 'function') {
      abortController = new AbortController();
    }

    const fetchOptions: RequestInit = {
      body: this.getRequestBody_(),
      headers: {
        ...(this.call_.request.headers !== undefined ? this.call_.request.headers : {}),
      },
      method: this.call_.request.method,
      signal: abortController ? abortController.signal : undefined,
    };

    return new Promise((resolve, reject): void => {
      let requestTimeout: any;
      requestTimeout = setTimeout((error: any) => {
        reject(
          createError(
            ErrorCodes.ERROR_CATEGORY_MASK_NETWORK,
            this.getErrorMessage('Request timed-out', requestUrl, error),
          ),
        );
        if (abortController) {
          abortController.abort();
        }
      }, TIMEOUT);

      const clearRequestTimeout = (): void => {
        if (requestTimeout) {
          clearTimeout(requestTimeout);
        }
        requestTimeout = undefined;
      };

      this.call_
        .execute(requestUrl, fetchOptions)
        .then(response => {
          clearRequestTimeout();
          resolve(response);
        })
        .catch(error => {
          clearRequestTimeout();
          reject(
            createError(
              ErrorCodes.ERROR_CATEGORY_MASK_NETWORK,
              this.getErrorMessage('Request failed', requestUrl, error instanceof Error ? error.stack : String(error)),
            ),
          );
        });
    });
  };
  private getErrorMessage(message: string, url: string, reason: any): string {
    return `${message}, url: ${url}, errorReason: ${reason}`;
  }

  private getRequestBody_ = (): Payload => {
    const requestBody = this.call_.request.body;
    switch (typeof requestBody) {
      case 'undefined':
        return undefined;
      case 'string':
        return requestBody;
      default:
        if (requestBody instanceof Blob || requestBody instanceof ArrayBuffer || requestBody instanceof FormData) {
          return requestBody;
        }
        return JSON.stringify(requestBody);
    }
  };
}
